import { ContractTariff, DataValue, LastSeenInfo, UsageData, ProRataData, RemoteSocket, RouterDetails, VoiceSimsDetails, WirelessLogicDetails, CommsPlanFeatures, RouterDevice } from "./"
import { Tele2Details } from "./entity/usage/Tele2Details"
import moment, { Moment } from 'moment'
import { LinkedSimData } from "./interfaces/linked-sim-details";
import { NetworkStatus } from "./interfaces/network-status";

const disallowedStatuses =  ['inactive', 'cancelled'];

export enum SimStatus {
  Active = "Active",
  Deactivated = "Deactivated",
	PendingDeactivation = "PendingDeactivation",
	PendingActivation = "PendingActivation",
  Retired = "Retired",
	Prepared = "Prepared",
	Unknown = "Unknown"
}

export function simStatusIsAllowed(status:string) {
	return disallowedStatuses.indexOf(status) == -1;
}

export class SimEssentials {
	customerId!:string
	provider!: string
	ocidNetwork!: string
	customerLabel!: string
	iccid!: string
	msisdn!: string
	imsi!: string
	ip_address!: string
	originalStatus?: string | undefined
	status!: string
	allowance!: DataValue
	proratedAllowance!: DataValue
	prorataDays?: number | undefined
	country?: string | undefined
	communicationPlan?: string | undefined

	uploaded!: DataValue
	downloaded!: DataValue
	smsUploaded!: DataValue
	smsDownloaded!: DataValue
	voiceUploaded!: DataValue
	voiceDownloaded!: DataValue

	// current usage to date (actual)
	total!: DataValue
	smsTotal!: DataValue
	voiceTotal!: DataValue

	// estimated usage for month (based on actual usage)
	totalEstimatedForMonth?: DataValue
	smsTotalEstimatedForMonth?: DataValue
	voiceTotalEstimatedForMonth?: DataValue

	// estimated usage to date (based on usage for month)
	totalEstimatedToDate?: DataValue
	smsTotalEstimatedToDate?: DataValue
	voiceTotalEstimatedToDate?: DataValue

	lastSeen!: Date | null
	daysSinceLastSeen!: number
	isOnline!: boolean
	ignore_notifications!: boolean
	deviceType!: string
	dateActivated?: Date | undefined
	daysActive?: number | undefined
	daysActiveThisMonth?: number | undefined
	dailyAverageUsage?: number | undefined
	ratePlan?: ContractTariff | undefined
	dtRatePlan?: ContractTariff | undefined
	notes?: string | undefined
	isFixedIP?: boolean | undefined

	contractTariff:ContractTariff | undefined

	includeInFlexiTariff: boolean
	purchaseOrderId: string | undefined

	isRemoteSocket: boolean
	remoteSocket: RemoteSocket | undefined

	linkedSim:LinkedSimData | undefined
	linkedSimFull: SimEssentials | undefined

	router:RouterDevice | undefined

	rat:Number | undefined

	connectivityAlarm: boolean | undefined
	connectivityAlarmEmail: string | undefined

	inNetworkExclusionCycle: boolean | undefined
	inNetworkSurveyCycle: boolean | undefined
	networkSurveyConfig: string | undefined
	allowSimSurvey: boolean = false
	cancelSurveyCycle: boolean = false
	commsPlanFeatureState: CommsPlanFeatures = CommsPlanFeatures.INELIGIBLE

	postgresOnlineNetworkStatus: NetworkStatus | undefined

	constructor(source: Tele2Details | WirelessLogicDetails | VoiceSimsDetails, contractTariffs:ContractTariff | ContractTariff[], routerDetails:RouterDevice[] | undefined = undefined) {

		this.constructTele2Essentials = this.constructTele2Essentials.bind(this)
		this.constructWirelessLogicEssentials = this.constructWirelessLogicEssentials.bind(this)
		this.findRouter = this.findRouter.bind(this)

		this.isRemoteSocket = false
		this.router = undefined
		
		switch(source.constructor.name) {
			case 'Tele2Details':
				this.constructTele2Essentials(source as Tele2Details, contractTariffs, routerDetails)
				break
			case 'WirelessLogicDetails':
				this.constructWirelessLogicEssentials(source as WirelessLogicDetails, contractTariffs)
				break
			case 'VoiceSimsDetails':
				this.constructVoiceSimEssentials(source as VoiceSimsDetails, contractTariffs)
				break
		}

		this.includeInFlexiTariff = false
		this.parseIncludeInFlexiTariff()
	}

	constructTele2Essentials(sim:Tele2Details, contractTariffs:ContractTariff | ContractTariff[], routerDetails:RouterDevice[] | undefined = undefined) {
		try {

			this.contractTariff = Array.isArray(contractTariffs) ? 
				contractTariffs.find( ct => {
					return ct.client_name == sim.accountCustom1 && ct.tariff.name == sim.accountCustom3
				}) : contractTariffs

			if(!this.contractTariff) return

			let lastSeen = this.getTele2LastSeen(sim.push_last_session_start, sim.push_last_session_end)

			let dateActivated = moment(sim.date_activated)
			let activationInfo = this.activationDetails(dateActivated)
			let dailyAverageUsage = sim.current_data_total / activationInfo.daysActiveToDate	
			let prorataDetails = this.getTele2ProratedTariffAllowance(sim, this.contractTariff)

			// Calculate usage data
			let usageData:UsageData = {}

			usageData.total      									= new DataValue( sim.current_data_total )
			usageData.smsTotal   									= new DataValue( sim.current_sms_total )
			usageData.voiceTotal 									= new DataValue( sim.current_voice_total )

			usageData.totalEstimatedForMonth      = new DataValue(( usageData.total.value / activationInfo.daysActiveToDate ) * activationInfo.daysActiveForMonth)
			usageData.smsTotalEstimatedForMonth   = new DataValue(( usageData.smsTotal.value / activationInfo.daysActiveToDate ) * activationInfo.daysActiveForMonth)
			usageData.voiceTotalEstimatedForMonth = new DataValue(( usageData.voiceTotal.value / activationInfo.daysActiveToDate ) * activationInfo.daysActiveForMonth)

			this.iccid          = sim.iccid
			this.msisdn         = sim.msisdn
			this.imsi						= sim.imsi
			this.ip_address     = this.int2ip(sim.ip_address)
			this.customerId     = sim.accountCustom1
			this.provider       = "Tele2"
			this.ocidNetwork    = sim.ocid_network
			this.customerLabel  = sim.custom_label || sim.accountCustom2
			
			this.originalStatus = sim.status
			this.status         = (() => {
				switch(sim.status) {
					case 'ACTIVATED'  : return SimStatus.Active
					case 'DEACTIVATED': return SimStatus.Deactivated
					case 'RETIRED'		: return SimStatus.Retired
					case 'ACTIVATION_READY':
					case 'TEST_READY'	: return SimStatus.Prepared
					default           : return SimStatus.Unknown
				}
			})(),
			this.allowance         = new DataValue(this.contractTariff?.tariff.data_value || 0)
			this.proratedAllowance = prorataDetails.allowance
			this.prorataDays       = prorataDetails.days
			this.communicationPlan = sim.communicationPlan
				
			this.uploaded        = new DataValue(0)
			this.downloaded      = new DataValue(0)
			this.smsUploaded     = new DataValue(0)
			this.smsDownloaded   = new DataValue(0)
			this.voiceUploaded   = new DataValue(0)
			this.voiceDownloaded = new DataValue(0)

			this.total      = usageData.total,
			this.smsTotal   = usageData.smsTotal,
			this.voiceTotal = usageData.voiceTotal,

			this.totalEstimatedForMonth      = usageData.totalEstimatedForMonth,
			this.smsTotalEstimatedForMonth   = usageData.smsTotalEstimatedForMonth,
			this.voiceTotalEstimatedForMonth = usageData.voiceTotalEstimatedForMonth,
			
			this.lastSeen             = lastSeen.lastSeenDate ? lastSeen.lastSeenDate.toDate(): null,
			this.daysSinceLastSeen    = lastSeen.daysSinceLastSeen,
			this.ignore_notifications = sim.ignore_notifications,
			this.deviceType           = sim.device_type,
			this.dateActivated        = dateActivated.toDate(),
			this.daysActive           = activationInfo.daysActive,
			this.daysActiveThisMonth  = activationInfo.daysActiveToDate,
			this.dailyAverageUsage    = dailyAverageUsage,
			this.isOnline             = sim.online_status
			this.purchaseOrderId			= sim.accountCustom4
			this.isFixedIP						= sim.is_fixed_ip
			this.isRemoteSocket				= sim.accountCustom6 == "ROCKET_SOCKET" ? true : false

			if(this.isRemoteSocket === true){
				this.remoteSocket = sim.remote_socket
			}

			this.linkedSim = {
				iccid: sim.linked_sim_iccid,
				tariffName: this.contractTariff.tariff.name
			}

			if(routerDetails) {
				this.router = this.findRouter(sim.iccid, routerDetails)
			}

			if(sim.connectivity_alarm != null && sim.connectivity_alarm_email != null) {
				this.connectivityAlarm = sim.connectivity_alarm
				this.connectivityAlarmEmail = sim.connectivity_alarm_email as string
			}

			this.inNetworkExclusionCycle = sim.in_network_exclusion_cycle
			this.inNetworkSurveyCycle = sim.in_survey_cycle
			this.networkSurveyConfig = sim.network_survey_config
			this.allowSimSurvey = sim.accountCustom8.toLowerCase() == 'survey'
			this.cancelSurveyCycle = sim.cancel_survey_cycle
			this.commsPlanFeatureState = sim.comms_plan_feature_status
			
		} catch(e) {
			console.log("Exception", e)
		}
	} 

	constructWirelessLogicEssentials(sim:WirelessLogicDetails, contractTariffs:ContractTariff | ContractTariff[]) {
		try {
			// Lookup the tariff for this sim
			this.contractTariff = Array.isArray(contractTariffs) ? 
				contractTariffs.find( ct => { 
					return ct.client_name == sim.custom_field1 && ct.tariff.name == sim.custom_field3
				}) : this.contractTariff
			if(!this.contractTariff) return

			// Last seen according to Wireless Logic
			let lastSeen = moment(sim.last_seen_detail != null ? new Date(sim.last_seen_detail) : new Date(0))
			let daysPassed = moment().diff(lastSeen, 'days')

			// Last seen according to internal calculations
			let internalLastSeenDate = new Date(sim.internal_last_seen)
			let internalLastSeen = isNaN(internalLastSeenDate.getTime()) ? new Date(0) : internalLastSeenDate
			let internalDaysPassed = moment().diff(internalLastSeen, 'days')

			let closestDaysPassed = Math.min(daysPassed, internalDaysPassed)

			// Get activatation date for pro-rata calculation
			let activationInfo = null
			if(sim.active_connection_activation_date) {
				let dateActivated = moment(sim.active_connection_activation_date)
				activationInfo = {
					dateActivated: dateActivated,
					...this.activationDetails(dateActivated)
				}
				activationInfo.dailyAverageUsage = sim.current_data_total / activationInfo.daysActiveToDate
			}
			let prorataDetails = this.getWLProratedTariffAllowance(sim, this.contractTariff)

			// Calculate usage data
			let usageData:UsageData = {}

			usageData.total      									= new DataValue( sim.current_data_total )
			usageData.smsTotal   									= new DataValue( sim.current_sms_total )
			usageData.voiceTotal 									= new DataValue( sim.current_voice_total )

			usageData.totalEstimatedForMonth      = new DataValue( ( usageData.total.value / activationInfo.daysActiveToDate ) * activationInfo.daysActiveForMonth)
			usageData.smsTotalEstimatedForMonth   = new DataValue( ( usageData.smsTotal.value / activationInfo.daysActiveToDate ) * activationInfo.daysActiveForMonth)
			usageData.voiceTotalEstimatedForMonth = new DataValue( ( usageData.voiceTotal.value / activationInfo.daysActiveToDate ) * activationInfo.daysActiveForMonth)

			this.customerId           = sim.custom_field1,
			this.provider             = "Wireless Logic",
			this.ocidNetwork          = sim.custom_field4,
			this.customerLabel        = sim.custom_label || sim.custom_field2,
			this.iccid                = sim.iccid,
			this.msisdn               = sim.active_connection_msisdn,
			this.imsi									= ""
			this.ip_address           = this.int2ip(parseInt(sim.active_connection_ip_address)),
			this.status               = (() => {
				switch(sim.active_connection_status) {
					case 'active'     : return SimStatus.Active
					case 'barred'     : return SimStatus.Deactivated
					case 'cancelled':
					case 'inactive_swapped':
					case 'inactive'		: return SimStatus.Retired
					default           : return SimStatus.Unknown
				}
			})(),
			this.allowance            = new DataValue(this.contractTariff.tariff.data_value),
			this.proratedAllowance    = prorataDetails.allowance,
			this.prorataDays          = prorataDetails.days

			this.uploaded             = new DataValue(parseInt(sim.current_data_up.toString())),
			this.downloaded           = new DataValue(parseInt(sim.current_data_down.toString())),
			this.smsUploaded          = new DataValue(0),
			this.smsDownloaded        = new DataValue(0),
			this.voiceUploaded        = new DataValue(0),
			this.voiceDownloaded      = new DataValue(0),

			this.total                = usageData.total,
			this.smsTotal             = usageData.smsTotal,
			this.voiceTotal           = usageData.voiceTotal,

			this.totalEstimatedForMonth      = usageData.totalEstimatedForMonth,
			this.smsTotalEstimatedForMonth   = usageData.smsTotalEstimatedForMonth,
			this.voiceTotalEstimatedForMonth = usageData.voiceTotalEstimatedForMonth,

			this.lastSeen             = internalLastSeen,
			this.daysSinceLastSeen    = closestDaysPassed,
			this.ignore_notifications = sim.ignore_notifications,
			this.deviceType           = sim.device_type,
			this.daysActive           = activationInfo.daysActive,
			this.daysActiveThisMonth  = activationInfo.daysActiveToDate,
			this.dailyAverageUsage    = activationInfo.dailyAverageUsage,
			this.isOnline             = true
			this.purchaseOrderId			= sim.custom_field4
			this.isFixedIP						= false
			this.linkedSim 						= {
				iccid: sim.linked_sim_iccid,
				tariffName: this.contractTariff.tariff.name
			}

		} catch(e) {
			console.log("Error:", e)
		}
	}

	constructVoiceSimEssentials(sim:VoiceSimsDetails, contractTariffs:ContractTariff | ContractTariff[]) {
		try {

			// Lookup the tariff for this sim
			this.contractTariff = Array.isArray(contractTariffs) ? 
				contractTariffs.find( ct => { 
					return ct.client_name == sim.client_name && ct.tariff.name == sim.tariff
				}) : contractTariffs
			if(!this.contractTariff) return

			this.customerId           = sim.client_name,
			this.provider             = "Voice",
			this.ocidNetwork          = sim.network,
			this.customerLabel        = sim.custom_label,
			this.iccid                = sim.iccid,
			this.msisdn               = sim.msisdn,
			this.imsi									= ""
			this.ip_address           = "0.0.0.0",
			this.status               = SimStatus.Active,
			this.allowance            = new DataValue(this.contractTariff.tariff.data_value),
			this.proratedAllowance    = new DataValue(0),
			this.prorataDays          = 0

			this.uploaded             = new DataValue(0),
			this.downloaded           = new DataValue(0),
			this.smsUploaded          = new DataValue(0),
			this.smsDownloaded        = new DataValue(0),
			this.voiceUploaded        = new DataValue(0),
			this.voiceDownloaded      = new DataValue(0),

			this.total                = new DataValue(0),
			this.smsTotal             = new DataValue(0),
			this.voiceTotal           = new DataValue(0),

			this.totalEstimatedForMonth      = new DataValue(0),
			this.smsTotalEstimatedForMonth   = new DataValue(0),
			this.voiceTotalEstimatedForMonth = new DataValue(0),

			this.lastSeen             = new Date(0),
			this.daysSinceLastSeen    = 0,
			this.ignore_notifications = true,
			this.deviceType           = sim.device_type,
			this.daysActive           = 0,
			this.daysActiveThisMonth  = 0,
			this.dailyAverageUsage    = 0,

			this.isOnline             = true,
			this.isFixedIP						= false

		} catch(e) {
			console.log("Error:", e)
		}
	}

	activationDetails(dateActivated:Moment):any {

		let daysActive = moment.duration(moment().diff(dateActivated)).as('days')

		let daysActiveToDate = this.isWithinThisMonth(dateActivated) ? 
				daysActive : 
				moment.duration(
					moment().diff(moment().startOf('month'))
				).as('days')
			
		let lastDayInMonth = moment().endOf('month')

		let daysActiveForMonth = this.isWithinThisMonth(dateActivated) ?
			moment.duration( lastDayInMonth.diff( dateActivated )).as('days') :
			moment.duration( moment().endOf('month').diff( moment().startOf('month') )).as('days')

		return {
			daysActive         : daysActive,
			daysActiveToDate   : daysActiveToDate,
			lastDayInMonth     : lastDayInMonth,
			daysActiveForMonth : daysActiveForMonth
		}
	}

	getTele2LastSeen(sessionStart:Date|null, sessionEnd:Date|null): LastSeenInfo {

		if(sessionStart == null && sessionEnd == null) return { 
			isOnline:false, 
			lastSeenDate:null, 
			daysSinceLastSeen:-1 
		}

		if(sessionEnd == null) {
			return {
				isOnline: true,
				lastSeenDate: sessionStart != null ? moment(sessionStart) : moment(),
				daysSinceLastSeen: 0
			}
		} else {
			return {
				isOnline: false,
				lastSeenDate: moment(sessionEnd),
				daysSinceLastSeen: moment(sessionEnd).diff(moment(), 'days')
			}
		}
	}

	isWithinThisMonth(date:Moment) {
		return moment().month() == date.month() && moment().year() == date.year()
	}

	findRouter(iccid:string, routerDetails:RouterDevice[]) {
		let router = routerDetails.find((router) => router.iccid == iccid)
		return router
	}

	getTele2ProratedTariffAllowance(sim:Tele2Details, ct:ContractTariff){
			
		let monthlyTariff:DataValue = new DataValue(ct.tariff.data_value)

		if(!sim.date_activated) { 
			return {
				allowance: monthlyTariff,
				days: moment().daysInMonth()
			}
		}
		
		if(moment().month() != moment(sim.date_activated).month() || moment().year() != moment(sim.date_activated).year()) { 
			return {
				allowance: monthlyTariff,
				days: moment().daysInMonth()
			}
		}

		let activationDate = moment(sim.date_activated);
		let daysInMonth = activationDate.daysInMonth();
		let dayInMonth = moment.duration(activationDate.diff(moment().startOf('month'))).as('days');

		let proratedAllowance = new DataValue( 
			monthlyTariff.value / daysInMonth * (daysInMonth - dayInMonth) 
		)

		return {
			allowance: proratedAllowance,
			days: daysInMonth - dayInMonth
		}

	}

	getWLProratedTariffAllowance(sim:WirelessLogicDetails, ct:ContractTariff){
			
		let monthlyTariff:DataValue = new DataValue(ct.tariff.data_value)

		if(!sim.active_connection_activation_date) { 
			return {
				allowance: monthlyTariff,
				days: moment().daysInMonth()
			}
		}
		
		if(moment().month() != moment(sim.active_connection_activation_date).month() || moment().year() != moment(sim.active_connection_activation_date).year()) { 
			return {
				allowance: monthlyTariff,
				days: moment().daysInMonth()
			}
		}

		let activationDate = moment(sim.active_connection_activation_date);
		let daysInMonth = activationDate.daysInMonth();
		let dayInMonth = moment.duration(activationDate.diff(moment().startOf('month'))).as('days');

		let proratedAllowance = new DataValue( 
			monthlyTariff.value / daysInMonth * (daysInMonth - dayInMonth) 
		)

		return {
			allowance: proratedAllowance,
			days: daysInMonth - dayInMonth
		}

	}

	parseIncludeInFlexiTariff() {
		if(this.contractTariff) {
			let include = this.contractTariff.tariff.include_in_flexi_tariff
			this.includeInFlexiTariff = this.contractTariff.include_in_flexi_tariff_override ? this.contractTariff.include_in_flexi_tariff : include
		}
	}

	int2ip(int:number) {
		var part1 = int & 255;
		var part2 = ((int >> 8) & 255);
		var part3 = ((int >> 16) & 255);
		var part4 = ((int >> 24) & 255);
	
		return part4 + "." + part3 + "." + part2 + "." + part1;
	}

	ip2int(ip:string) {
		return ip.split('.').reduce(function(ipInt, octet) { return (ipInt<<8) + parseInt(octet, 10)}, 0) >>> 0;
	}
}